'use client';

import React, { useState } from 'react';
import { X } from 'lucide-react';
import Link from 'next/link';

import { cn } from '@skybeam/shared-utils/classnames/cn';
import { Button } from '@skybeam/shared-ui-kit/ui/button';
import { NavItem } from './nav-item';
import { ReactComponent as Logo } from '../../../../public/skybeam-logo-with-text-blue.svg';

import { CircleSmall } from 'lucide-react';
import { ChevronDown } from 'lucide-react';

import styles from './navbar.module.css';

interface NavbarMobileProps {
  navItems: { link: string; label: string }[];
  industrySubItems: {
    link: string;
    label: string;
    icon: React.ReactNode;
    featuredSrc?: string;
    featuredTitle?: string;
  }[];
  navbarMode: 'light' | 'dark' | 'white';
  setClose: VoidFunction;
}

export const NavbarMobile: React.FC<NavbarMobileProps> = ({
  navItems,
  industrySubItems,
  navbarMode,
  setClose,
}) => {
  const dashboardUrl = process.env.NEXT_PUBLIC_DASHBOARD_URL;

  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <div
        data-test-mobile-navbar
        className={cn(
          styles.navbarMobile,
          'tablet:py-5 z-[100] flex-col rounded-b-[16px] border-b border-t border-transparent px-10 py-2 transition-colors duration-500',
          styles[navbarMode],
        )}
      >
        <div className="tablet:h-[38px] flex h-[34px] items-center justify-between">
          <div className="tablet:gap-x-8 flex h-8 items-center gap-x-4">
            <X className={styles.closeButton} onClick={setClose} />
            <Link href="/">
              <Logo id="skybeam-logo" className={styles.mobileLogo} />
            </Link>
          </div>
          <div className="tablet:flex hidden justify-center gap-x-2">
            <Link
              href={`${process.env.NEXT_PUBLIC_HUBSPOT_URL}`}
              className={cn(styles.link)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="link"
                className={cn(
                  'body-medium h-[38px] hover:no-underline',
                  navbarMode === 'white' ? 'text-zinc-950' : 'text-white',
                )}
              >
                Book a Demo
              </Button>
            </Link>
            <Link href={`${dashboardUrl}/sign-up`}>
              <Button
                className={cn(
                  'body-medium h-[38px] rounded-lg',
                  navbarMode === 'dark'
                    ? 'marketing-primary-button'
                    : 'marketing-tertiary-button',
                )}
                variant="secondary"
              >
                Start Now
              </Button>
            </Link>
          </div>
        </div>
        <div className="flex flex-col pt-5">
          {navItems.map(({ label, link }) => {
            if (link) {
              return (
                <NavItem key={link} href={link} onClick={setClose}>
                  {label}
                </NavItem>
              );
            }
            return (
              <div
                key={label}
                className={cn(
                  'font-proximaNova border-t border-gray-600 py-3 text-base',
                  styles.navLink,
                )}
                onClick={() => setIsExpanded((prev) => !prev)}
              >
                <div className="flex items-center gap-1">
                  {label}
                  <ChevronDown className={cn(styles.arrowDown, 'size-4')} />
                </div>

                {isExpanded && (
                  <div className="flex flex-col">
                    {industrySubItems.map(({ label, link, icon }) => (
                      <Link
                        key={label}
                        className="flex items-center gap-x-3 py-3"
                        href={link}
                        onClick={setClose}
                      >
                        <div className="flex size-8 items-center justify-center rounded-sm border border-blue-500 bg-blue-700">
                          {icon}
                        </div>
                        <span>{label}</span>
                        {!link && (
                          <div
                            className={cn(
                              'flex items-center gap-x-0.5 rounded-md py-0.5 pl-1 pr-2 text-sm',
                              navbarMode === 'light'
                                ? 'bg-blue-800'
                                : 'bg-blue-950',
                            )}
                          >
                            <CircleSmall className="size-3 fill-lime-200 text-lime-200" />
                            <span className="text-white">Soon</span>
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="tablet:hidden flex justify-center gap-x-2">
          <Link
            href={`${process.env.NEXT_PUBLIC_HUBSPOT_URL}`}
            className={cn(styles.link)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              data-test="Book a Demo-btn"
              className={cn(
                'body-medium h-9 w-full hover:no-underline',
                navbarMode === 'white' ? 'text-zinc-950' : 'text-white',
              )}
              variant="link"
            >
              Book a Demo
            </Button>
          </Link>
          <Link href={`${dashboardUrl}/sign-up`}>
            <Button
              data-test="Start Now-btn"
              className={cn(
                'body-medium w-full rounded-lg',
                navbarMode === 'dark'
                  ? 'marketing-primary-button'
                  : 'marketing-tertiary-button',
              )}
              variant="secondary"
            >
              Start Now
            </Button>
          </Link>
        </div>
      </div>
      <div
        className="fixed inset-0 z-[9] bg-black bg-opacity-50"
        onClick={setClose}
      />
    </>
  );
};
